
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Code, Database, Server, Cloud, Brain, ArrowRight, Sparkles } from 'lucide-react';
import servicesData from '@/data/services.json';

interface Service {
  id: string
  title: string
  description: string
  icon_name: string
  features: string[]
  technologies: string[]
  slug: string
  display_order: number
  is_active: boolean
}

const iconMap = {
  Code: Code,
  Database: Database,
  Server: Server,
  Cloud: Cloud,
  Brain: Brain,
}

const ServiceOverview = () => {
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Load static data
    const data = (servicesData as Service[])
      .filter(s => s.is_active)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    setServices(data)
    setLoading(false)
  }, [])

  if (loading) {
    return (
      <section className="section-padding bg-background">
        <div className="container-custom">
          <div className="text-center">
            <div className="animate-pulse text-muted-foreground">Loading services...</div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="section-padding bg-background relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-accent/20 to-transparent"></div>
      
      <div className="container-custom relative z-10">
        <div className="text-center max-w-4xl mx-auto mb-20 animate-on-scroll">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Sparkles size={16} className="mr-2" />
            Our Expertise
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8">
            Comprehensive <span className="gradient-text">IT Solutions</span>
          </h2>
          
          <p className="text-xl text-muted-foreground leading-relaxed">
            We deliver comprehensive IT solutions tailored to meet the unique needs of your business, 
            helping you stay ahead in the digital landscape.
          </p>
        </div>

        {services.length === 0 ? (
          <div className="text-center text-muted-foreground">
            <p>No services found. Please run the data fetch script to populate content.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon_name as keyof typeof iconMap] || Code
              
              return (
                <div 
                  key={service.id} 
                  className="group glass-card rounded-3xl p-8 card-hover animate-on-scroll"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="mb-6 relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-blue-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent size={32} className="text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-primary to-blue-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>
                  
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <Link 
                    to="/services" 
                    className="inline-flex items-center text-primary font-medium group-hover:underline transition-all"
                  >
                    Learn More
                    <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              )
            })}
          </div>
        )}
        
        <div className="text-center">
          <Link to="/services" className="btn-primary text-lg px-10 py-5">
            Explore All Services
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServiceOverview;
